<template>
  <div class="full-height-important relative">
    <BackButton v-if="backButton" @back="emit('back')" />

    <h1 class="theme-1 mb-3 text-2xl font-semibold leading-12">{{ t("pages.auth.sign-up.title") }}</h1>

    <p class="mb-3 text-sm leading-6">{{ t("pages.auth.sign-up.description") }}</p>

    <form @submit.prevent="submitSignUp">
      <div class="flex min-h-[21rem] flex-col justify-between">
        <div>
          <div class="mb-4">
            <IAIInputText id="first-name" v-model="newUser.email" autocomplete="given-name" disabled />
          </div>

          <div class="mb-4">
            <IAIInputText
              id="first-name"
              v-model="newUser.firstName"
              :placeholder="t('pages.auth.sign-up.first-name')"
              :rules="rules.empty(t('pages.auth.sign-up.first-name'))"
              autocomplete="given-name"
              focus-on-init
            />
          </div>

          <div class="mb-4">
            <IAIInputText
              id="last-name"
              v-model="newUser.lastName"
              :rules="rules.empty(t('pages.auth.sign-up.last-name'))"
              :placeholder="t('pages.auth.sign-up.last-name')"
              autocomplete="family-name"
            />
          </div>

          <div>
            <IAICheckbox id="terms" v-model="newUser.terms" :rules="rules.termsOfUse">
              <span>{{ t("pages.auth.sign-up.i-agree-to-terms") }}</span>

              <IAILink
                class="ml-1 text-theme-3"
                :text="t('pages.auth.sign-up.terms-of-use')"
                url="https://imagen-ai.com/terms-of-use/"
                tabindex="-1"
              />
            </IAICheckbox>
          </div>

          <div class="flex justify-center">
            <IAISpinner :invisible="!loading" />
          </div>

          <div v-if="generalError" class="text-theme-error">
            <span> {{ t("pages.auth.sign-up.general-error-prefix") }} </span>

            <IAILink
              class="ml-2 mr-2 text-theme-error"
              :text="t('pages.auth.sign-up.contact-support')"
              url="https://support.imagen-ai.com/hc/en-us/requests/new?ticket_form_id=23280822564381"
            />

            <span>{{ t("pages.auth.sign-up.general-error-suffix") }} </span>
          </div>
        </div>

        <IAIButton
          type="submit"
          :disabled="formCurrentlyInvalid || loading"
          class="mt-5 w-full"
          :label="t('pages.auth.sign-up.sign-up-cta')"
          variant="primary"
        />
      </div>
    </form>
  </div>
</template>

<script setup lang="ts">
import { t } from "@/locales/vue";
import { IAIButton, IAIInputText, IAIUseForm, IAICheckbox, IAISpinner, IAILink } from "@/core-ui-vue";
import { ref, reactive } from "vue";
import rules from "../common/rules";
import { sendOTP, signup } from "../services/api";
import BackButton from "./BackButton.vue";
import type { UserFlow } from "@/features/auth/services/api";

const props = withDefaults(
  defineProps<{
    email: string;
    userFlow?: UserFlow;
    backButton: boolean;
  }>(),
  {
    backButton: true,
  }
);

const emit = defineEmits<{
  (event: "signUp", response: "CODE_SENT", email: string): void;
  (event: "back"): void;
}>();

const loading = ref(false);
const generalError = ref(false);

const newUser = reactive({
  email: props.email,
  firstName: "",
  lastName: "",
  terms: false,
});

const { formRef, currentlyInvalid: formCurrentlyInvalid } = IAIUseForm({});

const submitSignUp = async () => {
  try {
    const isFormValid = (await formRef.validate()).valid;
    if (!isFormValid) return;
    loading.value = true;
    await signup(newUser.firstName, newUser.lastName, newUser.email, { userFlow: props.userFlow });
    await sendOTP(newUser.email);
    emit("signUp", "CODE_SENT", newUser.email);
    loading.value = false;
  } catch (err) {
    loading.value = false;
    generalError.value = true;
    console.error(err);
  }
};
</script>

<style scoped lang="scss">
.full-height-important {
  height: 100%;
}
</style>
